import { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Routes from "models/routes";
import { requestPasswordReset } from "utils/user/passwordResetApi";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import LanguageMenu from "components/common/ui/LanguageMenu";
import {
  LanguageMenuCorner,
  LoginButton,
  LoginMessageText,
  LoginPageContainer,
  LoginPageLink,
  LoginTextField,
  LoginTitle,
  LoginTitleSub,
} from "components/login/ui/loginPageStyles";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ForgotPasswordPageProps {}

const ForgotPasswordPage: FC<ForgotPasswordPageProps> = () => {
  const { t } = useTranslation();
  const promptSnackBar = usePromptSnackBar();
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      setEmailError(null);
    },
    [],
  );

  const handleSendClick = useCallback(() => {
    if (!EMAIL_PATTERN.test(email)) {
      setEmailError(t("Please enter a valid email address."));
      return;
    }
    setSending(true);
    requestPasswordReset(email)
      .then(() => {
        setSubmitted(true);
      })
      .catch(() => {
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: t("Failed to send the reset link. Please try again."),
          severity: "error",
          autoHeightDuration: 5000,
        });
      })
      .finally(() => {
        setSending(false);
      });
  }, [email, promptSnackBar, t]);

  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleSendClick();
      }
    },
    [handleSendClick],
  );

  return (
    <LoginPageContainer>
      <LanguageMenuCorner>
        <LanguageMenu />
      </LanguageMenuCorner>
      <LoginTitle>Chef Jasper</LoginTitle>
      <LoginTitleSub>{t("Reset your password")}</LoginTitleSub>
      {submitted ? (
        <LoginMessageText>
          {t(
            "If an account exists for {{email}}, a password reset link has been sent.",
            { email },
          )}
        </LoginMessageText>
      ) : (
        <>
          <LoginTextField
            label={t("Email")}
            value={email}
            error={emailError !== null}
            helperText={emailError}
            onChange={handleEmailChange}
            onKeyDown={handleKeypress}
          />
          <LoginButton
            text={t("Send reset link")}
            positive
            disabled={sending}
            onClick={handleSendClick}
          />
        </>
      )}
      <LoginPageLink to={Routes.LOGIN}>{t("Back to sign in")}</LoginPageLink>
    </LoginPageContainer>
  );
};

export default ForgotPasswordPage;
